/**
 * ElevenLabs Music Integration for Lucid Cyber RPG
 *
 * Responsibilities:
 * - Generate cartridge-themed background music tracks via ElevenLabs Music API
 * - Maintain a single game-wide playlist grouped by cartridge (per-cartridge set of tracks)
 * - Auto-generate new music when new cartridges are created
 * - Persist best-effort in localStorage; attempt Supabase storage if configured
 */

import { GameCartridge } from '@/types';
import { storageService } from './storageService';

type MusicTrack = {
  id: string;
  title: string;
  cartridgeId: string;
  duration: number; // seconds
  mood: string;
  style: string;
  url: string; // public or blob URL
  createdAt: string;
};

class ElevenLabsMusicService {
  private static instance: ElevenLabsMusicService;
  private baseUrl = 'https://api.elevenlabs.io/v1';
  private apiKey: string;

  static getInstance(): ElevenLabsMusicService {
    if (!this.instance) this.instance = new ElevenLabsMusicService();
    return this.instance;
  }

  private constructor() {
    const env: any = (import.meta as any).env || {};
    this.apiKey = env.VITE_ELEVENLABS_API_TOKEN || env.ELEVENLABS_API_TOKEN || '';
  }

  /**
   * Return playlist (tracks) for a cartridge, generating on-demand if missing.
   */
  async ensurePlaylistForCartridge(cartridge: GameCartridge, opts?: { numTracks?: number; duration?: number; styleHint?: string; mood?: string }): Promise<MusicTrack[]> {
    const key = this.playlistKey(cartridge.id);
    const cached = this.getLocalPlaylist(key);
    if (cached && cached.length) return cached;

    const count = Math.max(1, Math.min(3, opts?.numTracks ?? 1));
    const tracks: MusicTrack[] = [];
    for (let i = 0; i < count; i++) {
      const t = await this.generateCartridgeTrack(cartridge, {
        duration: opts?.duration ?? 60,
        styleHint: opts?.styleHint,
        mood: opts?.mood,
        seed: Math.floor(Math.random() * 1e9),
      });
      if (t) tracks.push(t);
    }

    if (tracks.length) this.setLocalPlaylist(key, tracks);
    return tracks;
  }

  getPlaylistForCartridge(cartridgeId: string): MusicTrack[] {
    return this.getLocalPlaylist(this.playlistKey(cartridgeId));
  }

  /**
   * Generate a single track themed to cartridge metadata.
   * Attempts ElevenLabs Music API; falls back to no-op if unavailable.
   */
  async generateCartridgeTrack(
    cartridge: GameCartridge,
    options: { duration?: number; styleHint?: string; mood?: string; seed?: number }
  ): Promise<MusicTrack | null> {
    const prompt = this.composeMusicPrompt(cartridge, options);
    const duration = Math.max(15, Math.min(180, options.duration ?? 60));

    try {
      if (!this.apiKey) {
        console.warn('[ElevenMusic] API key not configured; skipping music generation');
        return null;
      }

      // Attempt music generation (binary audio). Endpoint may evolve; handled via try/catch.
      // If ElevenLabs Music returns polling-based job ID in future, this should be adapted.
      const response = await fetch(`${this.baseUrl}/music/generate`, {
        method: 'POST',
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt,
          duration_seconds: duration,
          // Optional hints
          style: options.styleHint || undefined,
          mood: options.mood || undefined,
          seed: options.seed || undefined,
          format: 'mp3'
        })
      });

      if (!response.ok) {
        const err = await response.text();
        console.warn('[ElevenMusic] Generation failed:', response.status, err);
        return null;
      }

      const blob = await response.blob();
      // Try to persist to Supabase storage for stable replay
      let publicUrl = '';
      try {
        const fileName = `music_${cartridge.id}_${Date.now()}.mp3`;
        const uploaded = await (storageService as any).supabase?.storage?.from?.('cached-images')?.upload?.(`music/${fileName}`, blob, {
          contentType: 'audio/mpeg', upsert: true
        });
        if (uploaded?.error) {
          // Fallback to blob URL when storage is not configured
          publicUrl = URL.createObjectURL(blob);
        } else {
          const { data: publicData } = (storageService as any).supabase.storage.from('cached-images').getPublicUrl(`music/${fileName}`);
          publicUrl = publicData?.publicUrl || URL.createObjectURL(blob);
        }
      } catch (e) {
        publicUrl = URL.createObjectURL(blob);
      }

      const track: MusicTrack = {
        id: `track_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        title: `${cartridge.name} Theme`,
        cartridgeId: cartridge.id,
        duration,
        mood: options.mood || 'cinematic',
        style: options.styleHint || 'cyberpunk ambient',
        url: publicUrl,
        createdAt: new Date().toISOString()
      };
      return track;
    } catch (error) {
      console.warn('[ElevenMusic] Generation error:', error);
      return null;
    }
  }

  private composeMusicPrompt(cartridge: GameCartridge, opts: { styleHint?: string; mood?: string }): string {
    const genre = cartridge.worldSettings?.setting || 'cyberpunk';
    const themes = (cartridge.worldSettings?.themes || []).join(', ');
    const tags = (cartridge.tags || []).slice(0, 5).join(', ');
    const mood = opts.mood || 'cinematic, atmospheric, immersive';
    const style = opts.styleHint || `${genre} ambient with subtle pulses`;
    return `Game background music for ${cartridge.name}. Style: ${style}. Mood: ${mood}. Themes: ${themes}. Tags: ${tags}. No vocals. Loop-friendly.`;
  }

  private playlistKey(cartridgeId: string) {
    return `music_playlist_${cartridgeId}`;
  }
  private getLocalPlaylist(key: string): MusicTrack[] {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  private setLocalPlaylist(key: string, tracks: MusicTrack[]) {
    try { localStorage.setItem(key, JSON.stringify(tracks)); } catch {}
  }
}

export const elevenLabsMusicService = ElevenLabsMusicService.getInstance();
export type { MusicTrack };

